import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Volume2, Play, Pause } from 'lucide-react'
import WaveSurfer from 'wavesurfer.js'

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5, 2]

interface AppVoicePlaybackProps {
  audioUrl?: string | null
  isLoading?: boolean
  isSupported?: boolean
  onToggle: () => void
  onPlaybackEnd?: () => void
  className?: string
}

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

export const AppVoicePlayback: React.FC<AppVoicePlaybackProps> = ({
  audioUrl,
  isLoading = false,
  isSupported = true,
  onToggle,
  onPlaybackEnd,
  className = '',
}) => {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLDivElement>(null)
  const wavesurferRef = useRef<WaveSurfer | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [speed, setSpeed] = useState(1)
  const [isWaveReady, setIsWaveReady] = useState(false)

  // Initialise / destroy WaveSurfer when audioUrl changes
  useEffect(() => {
    if (!audioUrl || !containerRef.current) return

    setIsWaveReady(false)
    setCurrentTime(0)
    setDuration(0)
    setIsPlaying(false)

    const ws = WaveSurfer.create({
      container: containerRef.current,
      url: audioUrl,
      waveColor: '#8b8b8b',
      progressColor: '#705af8',
      height: 20,
      barWidth: 2,
      barGap: 4,
      barRadius: 1,
      interact: true,
      normalize: true,
    })

    ws.on('ready', (dur) => {
      setDuration(dur)
      setIsWaveReady(true)
      ws.setPlaybackRate(speed, true)
      ws.play()
    })

    ws.on('audioprocess', (t) => {
      setCurrentTime(t)
    })

    ws.on('play', () => setIsPlaying(true))
    ws.on('pause', () => setIsPlaying(false))

    ws.on('finish', () => {
      setIsPlaying(false)
      setCurrentTime(0)
      onPlaybackEnd?.()
    })

    wavesurferRef.current = ws

    return () => {
      ws.destroy()
      wavesurferRef.current = null
    }
    // speed intentionally omitted — handled separately via setPlaybackRate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioUrl])

  // Apply speed changes live without restarting
  useEffect(() => {
    wavesurferRef.current?.setPlaybackRate(speed, true)
  }, [speed])

  // Redraw waveform when container width changes (after duration is known and minWidth expands)
  useEffect(() => {
    if (duration > 0 && wavesurferRef.current) {
      const ws = wavesurferRef.current as WaveSurfer & { drawBuffer?: () => void }
      requestAnimationFrame(() => ws.drawBuffer?.())
    }
  }, [duration])

  const handlePlayPause = useCallback(() => {
    wavesurferRef.current?.playPause()
  }, [])

  const handleSpeedClick = useCallback(() => {
    const next = SPEED_OPTIONS[(SPEED_OPTIONS.indexOf(speed) + 1) % SPEED_OPTIONS.length]
    setSpeed(next)
  }, [speed])

  const speedLabel = speed === 1 ? '1x' : `${speed}x`
  const hasAudio = !!audioUrl

  // Waveform min-width scales with duration (~24px per second) so longer audio gets a wider waveform
  const waveformMinWidth =
    duration > 0 ? Math.min(400, Math.max(140, Math.round(duration * 24))) : 140

  // ── Idle state: no audio loaded yet (Figma: trigger button) ───────────────
  if (!hasAudio) {
    return (
      <div className={`flex items-center ${className}`}>
        {isLoading ? (
          <button
            type="button"
            disabled
            className="p-1.5 flex items-center justify-center text-[#8b8b8b]"
            aria-label={t('askCL.voice.loading')}
          >
            <Loader2 className="w-4 h-4 animate-spin" />
          </button>
        ) : (
          <button
            type="button"
            onClick={isSupported ? onToggle : undefined}
            disabled={!isSupported}
            className={`p-1.5 flex items-center justify-center transition-colors ${
              !isSupported
                ? 'text-app-grey-50 cursor-not-allowed'
                : 'text-[#8b8b8b] hover:text-primary-appBrand'
            }`}
            aria-label={!isSupported ? t('askCL.voice.unavailable') : t('askCL.voice.play')}
          >
            <Volume2 className="w-4 h-4" />
          </button>
        )}
      </div>
    )
  }

  // ── Active player (Figma: Voice message – white bar, purple accent) ───────
  return (
    <div className={`flex items-center ${className}`}>
      <div
        className="flex items-center justify-between gap-2 bg-white border border-[#e6e6e6] rounded-lg pl-2.5 pr-3 py-2 w-full max-w-full"
        data-name="Voice message"
      >
        {/* Play / Pause – purple circle, white icon (Figma: Button) */}
        <button
          type="button"
          onClick={handlePlayPause}
          className="rounded-full p-1.5 flex items-center justify-center bg-primary-appBrand text-white flex-shrink-0 transition-opacity hover:opacity-90"
          aria-label={isPlaying ? t('askCL.voice.pause') : t('askCL.voice.resume')}
        >
          {!isWaveReady ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : isPlaying ? (
            <Pause stroke={'white'} strokeWidth={2} className="w-4 h-4" />
          ) : (
            <Play stroke={'white'} strokeWidth={2} className="w-4 h-4" />
          )}
        </button>

        {/* Waveform – width scales with duration so longer audio has a wider waveform */}
        <div
          ref={containerRef}
          className="flex-1 min-w-0 h-5"
          style={{ minWidth: `${waveformMinWidth}px` }}
        />

        {/* Duration – Inter 14px regular #18181b (Figma: 0:17) */}
        <span className="text-sm font-normal text-[#18181b] flex-shrink-0 tabular-nums whitespace-nowrap leading-5">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>

        {/* Speed – grey pill, purple text (Figma: Button 1x) */}
        <button
          type="button"
          onClick={handleSpeedClick}
          className="text-sm font-medium text-primary-appBrand bg-[#ececec] hover:bg-[#e0e0e0] rounded-lg px-3 py-1.5 transition-colors flex-shrink-0"
          aria-label={t('askCL.voice.speed', { speed: speedLabel })}
        >
          {speedLabel}
        </button>
      </div>
    </div>
  )
}
